import { Link } from "react-router-dom";
import { useCopy, useFramework } from "../framework";
import type { GlyphMeaning } from "@apf/design-system/glyphs";
import { ArrowLinkLabel, Door, PageHeader, SectionHeading, ordinal } from "../components/ui";
import { useRevealOnce, useScrollToHash } from "./support";

/**
 * The two section landings introduced with the practitioner-first
 * information architecture. Each derives its onward doors from the same
 * governed `sectionNavigation` route metadata that drives the sticky rail,
 * so a change of section membership needs no page code.
 */

const doorGlyphs: Record<string, GlyphMeaning> = {
  "why-groundworks": "class.proof",
  "direct-material-economics": "domain.value",
  "indirect-buying": "domain.buying",
  "work-moments": "class.job",
  "capability-map": "utility.map",
  "connected-work": "class.coexistence",
  "workblock-design": "utility.grid",
  "workblock-architecture": "class.record",
  architecture: "utility.grid",
  workblocks: "object.workblock",
  design: "utility.grid",
  downloads: "class.record",
};

function SectionDoors({ sectionId, title, variant }: { sectionId: string; title: string; variant?: "bento" }) {
  const { snapshot } = useFramework();
  const copy = useCopy();
  const reveal = useRevealOnce<HTMLDivElement>();
  const doors = snapshot.content.routes
    .flatMap((route) => {
      const placement = route.sectionNavigation?.find(
        (candidate) => candidate.sectionId === sectionId,
      );
      return placement && route.id !== sectionId ? [{ route, order: placement.order }] : [];
    })
    .sort((left, right) => left.order - right.order);
  /* The bento variant belongs to the marketing register alone; the ruled
   * list stays for reference-register landings. */
  const listClass = variant === "bento" ? `apf-door-list apf-door-list--grid${reveal.revealClass}` : "apf-door-list";
  return (
    <section className="apf-page-section" aria-labelledby={`${sectionId}-doors-title`}>
      <SectionHeading id={`${sectionId}-doors-title`} title={title} />
      <div className={listClass} ref={variant === "bento" ? reveal.ref : undefined}>
        {doors.map(({ route }, index) => (
          <Door
            key={route.id}
            to={route.path}
            glyph={doorGlyphs[route.id] ?? "class.record"}
            index={ordinal(index + 1)}
            title={copy(route.labelKey)}
            line={copy(`page.${sectionId}.door.${route.id}`, copy(route.labelKey))}
          />
        ))}
      </div>
    </section>
  );
}

function NumberedSteps({
  idPrefix,
  count,
  variant,
}: {
  idPrefix: string;
  count: number;
  /** Cards render the sequence as elevated editorial tiles; marketing register only. */
  variant?: "cards";
}) {
  const copy = useCopy();
  const reveal = useRevealOnce<HTMLOListElement>();
  const listClass = variant === "cards"
    ? `apf-section-landing__steps apf-section-landing__steps--cards${reveal.revealClass}`
    : "apf-section-landing__steps";
  return (
    <ol className={listClass} ref={variant === "cards" ? reveal.ref : undefined}>
      {Array.from({ length: count }, (_, index) => {
        const step = index + 1;
        return (
          <li className="apf-section-landing__step" key={step}>
            <span className="apf-section-landing__ordinal">{ordinal(step)}</span>
            <div>
              <h3>{copy(`${idPrefix}.step${step}.title`)}</h3>
              <p>{copy(`${idPrefix}.step${step}.body`)}</p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

export function ClientEngagementPage() {
  const copy = useCopy();
  useScrollToHash();
  return (
    <div className="apf-page apf-register-marketing apf-section-landing">
      <PageHeader
        eyebrow={copy("page.client-engagement.eyebrow", "Client engagement")}
        title={copy("page.client-engagement.title", "Make the client case, then choose the proof.")}
        description={copy("page.client-engagement.description")}
      />
      <section className="apf-page-section" aria-labelledby="engagement-move-title">
        <SectionHeading
          id="engagement-move-title"
          title={copy("page.client-engagement.move.title", "The first credible move")}
        />
        <p className="apf-section-landing__lede apf-section-landing__lede--display">{copy("page.client-engagement.move.body")}</p>
      </section>
      <section className="apf-page-section apf-band apf-band--tint" aria-labelledby="engagement-sequence-title">
        <SectionHeading
          id="engagement-sequence-title"
          title={copy("page.client-engagement.sequence.title", "The engagement sequence")}
        />
        <NumberedSteps idPrefix="page.client-engagement.sequence" count={4} variant="cards" />
      </section>
      <SectionDoors
        sectionId="client-engagement"
        title={copy("page.client-engagement.doors.title", "Where to go next")}
        variant="bento"
      />
      <p className="apf-section-landing__next">
        <Link to="/build">
          <ArrowLinkLabel>{copy("page.client-engagement.next", "Next: Build. From one client outcome to a versioned Workblock.")}</ArrowLinkLabel>
        </Link>
      </p>
    </div>
  );
}

export function BuildPage() {
  const copy = useCopy();
  useScrollToHash();
  return (
    <div className="apf-page apf-register-reference apf-section-landing">
      <PageHeader
        eyebrow={copy("page.build.eyebrow", "Build")}
        title={copy("page.build.title", "From one client outcome to a versioned Workblock.")}
        description={copy("page.build.description")}
      />
      <section className="apf-page-section" id="delivery-motion" aria-labelledby="build-method-title">
        <SectionHeading
          id="build-method-title"
          title={copy("page.build.method.title", "The delivery motion")}
          description={copy("page.build.method.description")}
        />
        <NumberedSteps idPrefix="page.build.method" count={5} />
        <p className="apf-section-landing__claim">
          {copy(
            "claim.days-not-weeks",
            "Workblocks are integrated with client systems in days, not weeks, and working Workblocks are built in the same timeframe.",
          )}
        </p>
      </section>
      <SectionDoors
        sectionId="build"
        title={copy("page.build.doors.title", "Start from governed material")}
      />
      <p className="apf-section-landing__next">
        <Link to="/reference">
          <ArrowLinkLabel>{copy("page.build.next", "Next: Reference. The complete governed reference.")}</ArrowLinkLabel>
        </Link>
      </p>
    </div>
  );
}
